import { useRef, useState } from 'react';
import type { ClearScope } from '@/lib/net';
import { useDismiss } from '../kit';
import {
  IconTarget, IconUndo, IconRedo, IconTrash, IconPlus, IconMinus, IconMore,
} from '../icons';

/**
 * Bottom-right cluster: find-my-way-back, undo, redo, clear, zoom.
 *
 * Collapsed to a single orb that opens out sideways, so it takes up as little
 * of the drawing as possible. The crosshair opens a short menu rather than
 * guessing which of the three possible destinations you meant — the canvas is
 * big enough that "take me back" is genuinely ambiguous.
 */
export default function ActionDock({
  canUndo, canRedo, isHost, allowGuestClearAll, hasContent,
  onUndo, onRedo, onClear, onGoMyWork, onGoStart, onFitAll, onZoom,
  confirmClear,
}: {
  canUndo: boolean;
  canRedo: boolean;
  isHost: boolean;
  allowGuestClearAll: boolean;
  hasContent: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onClear: (scope: ClearScope) => void;
  onGoMyWork: () => void;
  onGoStart: () => void;
  onFitAll: () => void;
  onZoom: (factor: number) => void;
  confirmClear: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState<'none' | 'locate' | 'clear'>('none');
  const rootRef = useRef<HTMLDivElement>(null);

  useDismiss([rootRef], () => { setMenu('none'); setOpen(false); }, open || menu !== 'none');

  const canClearAll = isHost || allowGuestClearAll;

  const CLEAR_PROMPTS: Record<ClearScope, string> = {
    'mine-drawing': 'Clear what you have drawn? Any images you added stay put.',
    'mine-images': 'Remove the images you added? Your drawing stays.',
    mine: 'Clear everything you have added to this sketchpad?',
    all: 'Clear the whole sketchpad for everyone? You can undo this from your own history.',
  };

  const doClear = (scope: ClearScope) => {
    setMenu('none');
    // eslint-disable-next-line no-alert
    if (confirmClear && !window.confirm(CLEAR_PROMPTS[scope])) return;
    onClear(scope);
  };

  return (
    <div ref={rootRef} className="pill pill--actions" style={{ position: 'relative' }}>
      <div className="expando" data-open={open}>
        <button type="button" className="pill-btn" onClick={() => onZoom(1 / 1.4)} aria-label="Zoom out" title="Zoom out">
          <IconMinus />
        </button>
        <button type="button" className="pill-btn" onClick={() => onZoom(1.4)} aria-label="Zoom in" title="Zoom in">
          <IconPlus />
        </button>

        <span className="pill-sep" />

        <button
          type="button"
          className="pill-btn"
          data-active={menu === 'locate'}
          onClick={() => setMenu((m) => (m === 'locate' ? 'none' : 'locate'))}
          aria-label="Find my drawing"
          title="Find my drawing"
        >
          <IconTarget />
        </button>

        <button
          type="button"
          className="pill-btn"
          onClick={onUndo}
          disabled={!canUndo}
          aria-label="Undo"
          title="Undo (⌘Z)"
        >
          <IconUndo />
        </button>

        <button
          type="button"
          className="pill-btn"
          onClick={onRedo}
          disabled={!canRedo}
          aria-label="Redo"
          title="Redo (⇧⌘Z)"
        >
          <IconRedo />
        </button>

        <button
          type="button"
          className="pill-btn"
          data-active={menu === 'clear'}
          onClick={() => setMenu((m) => (m === 'clear' ? 'none' : 'clear'))}
          aria-label="Clear"
          title="Clear"
        >
          <IconTrash />
        </button>
      </div>

      <button
        type="button"
        className="pill-btn"
        data-active={open}
        onClick={() => { setOpen((v) => !v); setMenu('none'); }}
        aria-expanded={open}
        aria-label={open ? 'Hide canvas controls' : 'Show canvas controls'}
        title="Canvas controls"
      >
        <IconMore />
      </button>

      {menu !== 'none' ? (
        <div className="popover popover--actions">
          {menu === 'locate' ? (
            <>
              <p className="popover__title">Take me to</p>
              <div style={{ display: 'grid', gap: 6 }}>
                <button
                  type="button"
                  className="btn btn--sm btn--ghost"
                  style={{ justifyContent: 'flex-start' }}
                  onClick={() => { setMenu('none'); onGoMyWork(); }}
                >
                  Where I last drew
                </button>
                <button
                  type="button"
                  className="btn btn--sm btn--ghost"
                  style={{ justifyContent: 'flex-start' }}
                  onClick={() => { setMenu('none'); onGoStart(); }}
                >
                  Back to the start
                </button>
                <button
                  type="button"
                  className="btn btn--sm btn--ghost"
                  style={{ justifyContent: 'flex-start' }}
                  onClick={() => { setMenu('none'); onFitAll(); }}
                  disabled={!hasContent}
                >
                  Fit the whole drawing
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="popover__title">Clear</p>
              <div style={{ display: 'grid', gap: 6 }}>
                <button
                  type="button"
                  className="btn btn--sm btn--ghost"
                  style={{ justifyContent: 'flex-start' }}
                  onClick={() => doClear('mine-drawing')}
                >
                  Just my drawing
                </button>
                <button
                  type="button"
                  className="btn btn--sm btn--ghost"
                  style={{ justifyContent: 'flex-start' }}
                  onClick={() => doClear('mine-images')}
                >
                  Just my images
                </button>
                <button
                  type="button"
                  className="btn btn--sm btn--ghost"
                  style={{ justifyContent: 'flex-start' }}
                  onClick={() => doClear('mine')}
                >
                  Everything I added
                </button>
                {canClearAll ? (
                  <button
                    type="button"
                    className="btn btn--sm btn--danger"
                    style={{ justifyContent: 'flex-start' }}
                    onClick={() => doClear('all')}
                  >
                    Everything, for everyone
                  </button>
                ) : null}
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
